import { X } from 'lucide-react';
import { Badge, Panel } from '@/design-system';
import { formatCount, formatRatioAsPercent } from '../funnel-editor/metrics/format';
import type { NodeMetricsResult } from '@/domain/metrics/node-metrics';

export interface PresentationMetricsPanelProps {
  nodeName: string;
  result: NodeMetricsResult | undefined;
  onClose: () => void;
}

/**
 * SPEC-011 §57-59/plan §28 — "show selected summary values ... without
 * opening editor property panels" (§58). Values come straight from the
 * SPEC-008 propagation result; this never recomputes or overrides them
 * (PRES-PRINCIPLE-003), and the manual/calculated origin stays visible via
 * the same badge tone convention as the editor's metrics UI.
 */
export function PresentationMetricsPanel({
  nodeName,
  result,
  onClose,
}: PresentationMetricsPanelProps) {
  return (
    <Panel
      className="absolute bottom-4 left-4 z-10 w-64 shadow-lg"
      role="status"
      aria-label={`Métricas de ${nodeName}`}
    >
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-medium text-text-primary">{nodeName}</h3>
        <button
          type="button"
          aria-label="Fechar métricas"
          onClick={onClose}
          className="text-text-muted hover:text-text-primary"
        >
          <X size={14} />
        </button>
      </div>
      {result ? (
        <dl className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
          <dt className="text-text-muted">Entradas</dt>
          <dd className="text-right text-text-secondary">
            {formatCount(result.entries.effectiveValue)}
          </dd>
          <dt className="text-text-muted">Saídas</dt>
          <dd className="text-right text-text-secondary">
            {formatCount(result.exits.effectiveValue)}
          </dd>
          <dt className="text-text-muted">Conversão</dt>
          <dd className="text-right text-text-secondary">
            {formatRatioAsPercent(result.conversion.effectiveValue)}
          </dd>
        </dl>
      ) : (
        <p className="text-xs text-text-muted">Não há métricas disponíveis.</p>
      )}
      <Badge className="mt-2">Motor de métricas (SPEC-008)</Badge>
    </Panel>
  );
}
